import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  createScheduleType,
  updateScheduleType,
  deleteScheduleType,
  replaceScheduleVariant,
  createPeriod,
  updatePeriod,
  deletePeriod,
  listPeriods,
  listSchoolPeriods,
  createSchoolPeriod,
  nextDisplayOrder,
} from '../../api/schoolConfigApi';
import { showSuccess, showError } from '../../utils/notifications';
import { getContextualError, logError } from '../../utils/errorHandling';
import ScheduleForm, {
  type ScheduleFormPeriod,
  type ScheduleFormAbDay,
} from './ScheduleForm';
import {
  getDefaultStartTime,
  getDefaultEndTime,
  timeToMinutes,
  findTimeOverlaps,
} from '../../utils/periodFormatHelper';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../common/Modal';
import { isSplitVariant } from '../../utils/scheduleVariant';
import styles from './ScheduleVariantModal.module.css';
import type { components } from '../../api/types.gen';
// Reuse the canonical variant/config shapes owned by the M7a panel that
// renders this modal. These are type-only imports (erased at runtime), so the
// panel -> modal runtime dependency stays one-directional despite the mutual
// type reference (the panel imports this component; this file only imports the
// panel's *types*).
import type { ScheduleVariant, SchoolConfig } from './panels/ScheduleConfigPanel';

// API/payload shapes come from the generated OpenAPI schema — never
// hand-duplicated. Schedule/variant/period rows map to these.
type PeriodResponse = components['schemas']['PeriodResponse'];
type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];
type PeriodListResponse = components['schemas']['PeriodListResponse'];
type CreatePeriodBody = components['schemas']['CreatePeriodBody'];

// The local A/B/Both/single tag carried on this modal's in-memory periods —
// used only for UI grouping (ScheduleForm groups by it); it has no direct
// backend field (a Period belongs to exactly one ScheduleType). Reuses
// ScheduleForm's exported tag type.
type AbDay = ScheduleFormAbDay;

/**
 * The in-memory period shape this modal manipulates. Reuses ScheduleForm's
 * exported `ScheduleFormPeriod` ({schoolPeriodId, start, end, abDay}) so the
 * form's periods/onPeriodsChange contract is honored exactly, and adds the
 * optional singleId/aId/bId that capture the real backend Period.id backing
 * each shape — set once at load time and carried forward through every toggle
 * (see the shape-conversion helpers below). `schoolPeriodId` may be null (a
 * freshly-added, not-yet-selected row) or a synthetic negative placeholder (a
 * pending canonical period — see resolveDefaultPeriods).
 */
type EditablePeriod = ScheduleFormPeriod & {
  singleId?: number | null;
  aId?: number | null;
  bId?: number | null;
};

/**
 * A period past handleSave's "every period needs a period selected"
 * validation: its schoolPeriodId is guaranteed non-null. Save-time helpers
 * that build create/update bodies operate on these so schoolPeriodId flows in
 * as a plain `number`, matching CreatePeriodBody.
 */
type ValidatedPeriod = EditablePeriod & { schoolPeriodId: number };

/** Name-keyed default proposal produced by generateDefaultPeriods. */
interface NamedPeriodProposal {
  name: string;
  start: string;
  end: string;
  abDay: AbDay;
}

/**
 * A canonical SchoolPeriod registry entry as tracked in local state. Widens
 * SchoolPeriodResponse's `displayOrder: number` to allow the in-memory-only
 * pending rows (synthetic negative id, null displayOrder) resolveDefaultPeriods
 * seeds before they're persisted.
 */
interface EditableSchoolPeriod {
  id: number;
  name: string;
  displayOrder: number | null;
}

/** One period's desired end-state fed to reconcilePeriods' id-based diff. */
interface DesiredPeriod {
  id: number | null;
  schoolPeriodId: number;
  start: string;
  end: string;
}

export type VariantModalMode = 'create' | 'edit' | 'duplicate';

/**
 * Public props contract for ScheduleVariantModal. Consumed by
 * ScheduleConfigPanel (M7a), which renders this modal — `mode` and
 * `existingVariant` are optional here because the component supplies defaults.
 */
export interface ScheduleVariantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVariantSaved: () => void;
  schoolConfig?: SchoolConfig | null;
  schoolId?: number | string | null;
  mode?: VariantModalMode;
  existingVariant?: ScheduleVariant | null;
}

// Migrated off the legacy `manageScheduleVariants` Firebase callable as part
// of the Firebase -> hallpass-backend migration. See
// ScheduleConfigPanel.js's header comment for the full "variant" concept
// this modal edits.
//
// MODEL MISMATCH: a legacy schedule variant was one document with a flat
// `periods` array, each period optionally tagged `abDay: 'A'|'B'|'Both'`.
// hallpass-backend has no such tag on Period — a Period belongs to exactly
// one ScheduleType (packages/db/prisma/schema.prisma), and there's no field
// for the legacy passBlockingStart/passBlockingEnd/limitlessOverride/
// includeInUploads (confirmed dropped, not re-added here). The chosen
// translation (matches ScheduleConfigPanel.js's groupScheduleTypes):
//   - Regular schools: one legacy variant -> one ScheduleType (abDay: null),
//     its periods created 1:1.
//   - Block/Mixed schools: one legacy variant -> TWO ScheduleTypes sharing
//     one `name`, distinguished by ScheduleType.abDay ('A'/'B'). A period
//     tagged abDay:'Both' (e.g. Advisory, Lunch) is duplicated into BOTH
//     day's period lists, since a period can only belong to one ScheduleType.
// This modal keeps the same in-memory `abDay`-tagged period shape for the
// UI (ScheduleForm still groups by it), but on load/save translates to/from
// the two-ScheduleType REST reality, and does an id-based diff (update in
// place / create / delete) against the Period rows already loaded from the
// server rather than blowing away and recreating everything on every save.

// Default periods for a brand-new variant, seeded from the school's
// schoolType/totalPeriods the same way the legacy modal did. Returns
// name-keyed proposals (not yet resolved against the canonical SchoolPeriod
// registry — see resolveDefaultPeriods, which turns these into real
// schoolPeriodIds before the form renders).
function generateDefaultPeriods(
  schoolConfig: SchoolConfig | null | undefined
): NamedPeriodProposal[] {
  if (!schoolConfig) return [];

  const periods: NamedPeriodProposal[] = [];
  const { schoolType } = schoolConfig;
  const totalPeriods = schoolConfig.totalPeriods ?? 0;

  if (schoolType === 'Regular') {
    for (let i = 1; i <= totalPeriods; i++) {
      periods.push({
        name: `Period ${i}`,
        start: getDefaultStartTime(i),
        end: getDefaultEndTime(i),
        abDay: null,
      });
    }
  } else if (schoolType === 'Block' || schoolType === 'Mixed') {
    const halfPeriods = Math.ceil(totalPeriods / 2);

    for (let i = 1; i <= halfPeriods; i++) {
      periods.push({
        name: `Period ${i}`,
        start: getDefaultStartTime(i),
        end: getDefaultEndTime(i),
        abDay: 'A',
      });
    }

    for (let i = halfPeriods + 1; i <= totalPeriods; i++) {
      const blockPosition = i - halfPeriods;
      periods.push({
        name: `Period ${i}`,
        start: getDefaultStartTime(blockPosition),
        end: getDefaultEndTime(blockPosition),
        abDay: 'B',
      });
    }
  }

  periods.push({
    name: 'Advisory',
    start: '11:30',
    end: '11:55',
    abDay: schoolType === 'Regular' ? null : 'Both',
  });

  periods.push({
    name: 'Lunch',
    start: '11:55',
    end: '12:25',
    abDay: schoolType === 'Regular' ? null : 'Both',
  });

  return periods;
}

function sortByOrder<T extends { order: number }>(periodList: T[]): T[] {
  return [...periodList].sort((a, b) => a.order - b.order);
}

// Whether a periods array is currently in split (A/B) shape, i.e. at least
// one period carries an abDay tag. Matches the isSplitVariant(group)
// precedent in ../../utils/scheduleVariant.js — same predicate shape, but
// over this modal's in-memory periods array rather than a loaded variant
// group's scheduleTypeIds, so it's kept local rather than imported.
function isSplitShape(periods: EditablePeriod[]): boolean {
  return periods.some((p) => p.abDay);
}

// The non-null ScheduleType ids backing a loaded variant, in single/aDay/bDay
// order. Shared by saveEdit's shape-conversion branch (which old
// ScheduleTypes to delete after creating the new shape) and handleDelete
// (which ScheduleTypes to delete outright). A variant's scheduleTypeIds are
// always numeric ids from the backend, so the non-null ones are `number`.
function scheduleTypeIdsOf({
  single,
  aDay,
  bDay,
}: {
  single?: number | null;
  aDay?: number | null;
  bDay?: number | null;
}): number[] {
  return [single, aDay, bDay].filter((id): id is number => id != null);
}

// Resolves generateDefaultPeriods' name-keyed proposals against the school's
// canonical SchoolPeriod registry — reusing an existing period by exact name
// match, or assigning an in-memory-only placeholder (synthetic negative id,
// never sent to the server) when no match exists yet. Purely in-memory —
// creation of any placeholder is deferred to resolvePendingSchoolPeriods,
// called only from saveAsNew, so merely opening (then cancelling) the modal
// never persists a canonical period the admin didn't ask for.
function resolveDefaultPeriods(
  existingSchoolPeriods: EditableSchoolPeriod[],
  namedPeriods: NamedPeriodProposal[]
): { resolved: EditablePeriod[]; schoolPeriods: EditableSchoolPeriod[] } {
  const byName = new Map(existingSchoolPeriods.map((sp) => [sp.name, sp]));
  const pendingSchoolPeriods: EditableSchoolPeriod[] = [];
  const resolved: EditablePeriod[] = [];
  let nextPendingId = -1;

  for (const p of namedPeriods) {
    let schoolPeriod = byName.get(p.name);
    if (!schoolPeriod) {
      schoolPeriod = { id: nextPendingId--, name: p.name, displayOrder: null };
      byName.set(p.name, schoolPeriod);
      pendingSchoolPeriods.push(schoolPeriod);
    }
    resolved.push({
      schoolPeriodId: schoolPeriod.id,
      start: p.start,
      end: p.end,
      abDay: p.abDay,
    });
  }

  return {
    resolved,
    schoolPeriods: [...existingSchoolPeriods, ...pendingSchoolPeriods],
  };
}

// Reconstructs the "Both" grouping on load: an A-day and a B-day period
// referencing the same schoolPeriodId are treated as one shared period.
// Anything left unmatched stays a plain 'A' or 'B' period.
function pairAAndBPeriods(
  aPeriods: PeriodResponse[],
  bPeriods: PeriodResponse[]
): EditablePeriod[] {
  const usedB = new Set<number>();
  const combined: EditablePeriod[] = [];

  aPeriods.forEach((ap) => {
    const matchIndex = bPeriods.findIndex(
      (bp, i) =>
        !usedB.has(i) &&
        bp.schoolPeriodId === ap.schoolPeriodId &&
        bp.startTime === ap.startTime &&
        bp.endTime === ap.endTime
    );

    if (matchIndex !== -1) {
      usedB.add(matchIndex);
      combined.push({
        schoolPeriodId: ap.schoolPeriodId,
        start: ap.startTime,
        end: ap.endTime,
        abDay: 'Both',
        aId: ap.id,
        bId: bPeriods[matchIndex].id,
      });
    } else {
      combined.push({
        schoolPeriodId: ap.schoolPeriodId,
        start: ap.startTime,
        end: ap.endTime,
        abDay: 'A',
        aId: ap.id,
      });
    }
  });

  bPeriods.forEach((bp, i) => {
    if (usedB.has(i)) return;
    combined.push({
      schoolPeriodId: bp.schoolPeriodId,
      start: bp.startTime,
      end: bp.endTime,
      abDay: 'B',
      bId: bp.id,
    });
  });

  return combined;
}

// expandToSplit and collapseToSingle are the two directions of the
// "Convert to Split/Single Schedule" toggle (handleToggleShape). Each
// period's real original identity (aId/bId for a split-stored Period pair,
// singleId for a single-stored Period) is captured ONCE, when the variant
// is first loaded (see the load effect below and pairAAndBPeriods), and
// carried forward unchanged on the period object itself through every
// subsequent toggle -- never re-derived from a schoolPeriodId lookup. A
// schoolPeriodId-keyed lookup is exactly what let two periods legitimately
// sharing one schoolPeriodId get silently swapped whenever the admin
// removed one of them before toggling away and back: position in a
// schoolPeriodId-grouped queue is not the same thing as being the same
// Period row. Identity by direct reference has no such ambiguity -- a
// period that survives edits/removals/toggles always carries its own real
// id(s), never someone else's. A period with no identity in a given shape
// (added fresh, or the variant was never stored that way) simply carries
// null for that shape's id field(s) and gets a fresh Period created for it
// on save -- there is nothing to recover, so there is nothing to get wrong.

// Produces a split-shape periods array from the CURRENT (possibly single)
// in-memory periods. A period's own aId/bId (set at load time by
// pairAAndBPeriods, or carried forward by an earlier expandToSplit)
// determine its restored tag: both present -> 'Both', only one present ->
// that side. Neither present means this period was never split before (a
// genuinely new period, or the variant was never stored as split) -- there
// is no history to recover, so it defaults to 'Both' (visible on both days,
// nothing hidden), the only non-lossy default. Current start/end are always
// kept (edits are never discarded); singleId is carried through unchanged
// so a later collapse back to single still recovers it.
function expandToSplit(periods: EditablePeriod[]): EditablePeriod[] {
  return periods.map((p) => {
    const { schoolPeriodId, start, end, aId = null, bId = null, singleId = null } = p;
    const abDay: AbDay =
      aId != null && bId != null
        ? 'Both'
        : aId != null
          ? 'A'
          : bId != null
            ? 'B'
            : 'Both';
    return { schoolPeriodId, start, end, abDay, aId, bId, singleId };
  });
}

// The single-target mirror of expandToSplit: produces a single-shape
// periods array from the CURRENT (possibly split) in-memory periods. B-only
// periods are always dropped (single storage has no A/B concept to
// preserve them in -- this is the one direction-inherent, unavoidable data
// loss, which is why handleToggleShape still gates it behind a
// window.confirm, unconditionally, BEFORE calling this). Each surviving
// period's own singleId (set at load time, or carried forward by an
// earlier collapseToSingle) recovers the real Period row so saveEdit's
// reconcile updates it in place; a period with no singleId (never stored as
// single) gets one created fresh. aId/bId are carried through unchanged so
// a later expand back to split still recovers them.
function collapseToSingle(periods: EditablePeriod[]): EditablePeriod[] {
  return periods
    .filter((p) => p.abDay !== 'B')
    .map((p) => {
      const { schoolPeriodId, start, end, aId = null, bId = null, singleId = null } = p;
      return { schoolPeriodId, start, end, abDay: null, singleId, aId, bId };
    });
}

// Id-based diff of one ScheduleType's Period rows against the desired list
// (in order) — updates rows that still exist, creates rows with no id yet,
// deletes rows that were dropped from the desired list. This is a sequence
// of independent REST calls (no backend transaction), so the ordering below
// matters: dropped rows are deleted LAST, after every update/create has
// succeeded, so a failure partway through an update/create (a validation
// error, or a 409 from the Period_scheduleTypeId_schoolPeriodId_key unique
// index) never leaves the variant with fewer periods than it started with —
// nothing has been removed yet at that point. Deleting first (the previous
// order) could commit those deletions before a later failure, permanently
// losing periods with no way to recover except manually re-adding them.
//
// One dropped row is deleted early (just-in-time, right before the
// update/create that needs its slot): if a kept/new period's target
// schoolPeriodId is still held by a not-yet-deleted dropped row (e.g. the
// admin removed a period and re-added a different row for the same
// schoolPeriodId in the same save), that update/create would otherwise 409
// against the still-live dropped row. Clearing only that one specific row
// immediately before its slot is claimed keeps every OTHER dropped row
// (the common case) deferred to the final pass.
async function reconcilePeriods(
  schoolId: number | string,
  scheduleTypeId: number | string,
  originalPeriods: PeriodResponse[],
  desiredPeriods: DesiredPeriod[]
): Promise<void> {
  const keepIds = new Set(
    desiredPeriods.map((p) => p.id).filter((id): id is number => id != null)
  );

  // schoolPeriodId -> original Period.id, for rows being dropped. The
  // Period_scheduleTypeId_schoolPeriodId_key unique index guarantees at most
  // one live Period per schoolPeriodId within a ScheduleType, so this map is
  // never ambiguous.
  const droppedIdBySchoolPeriodId = new Map<number, number>();
  for (const original of originalPeriods) {
    if (!keepIds.has(original.id)) {
      droppedIdBySchoolPeriodId.set(original.schoolPeriodId, original.id);
    }
  }

  const deletedIds = new Set<number>();
  const clearStaleSlot = async (schoolPeriodId: number): Promise<void> => {
    const staleId = droppedIdBySchoolPeriodId.get(schoolPeriodId);
    if (staleId != null && !deletedIds.has(staleId)) {
      await deletePeriod(schoolId, scheduleTypeId, staleId);
      deletedIds.add(staleId);
    }
  };

  for (let i = 0; i < desiredPeriods.length; i++) {
    const desired = desiredPeriods[i];
    const body = {
      schoolPeriodId: desired.schoolPeriodId,
      startTime: desired.start,
      endTime: desired.end,
      order: i + 1,
    };

    await clearStaleSlot(desired.schoolPeriodId);

    if (desired.id != null) {
      await updatePeriod(schoolId, scheduleTypeId, desired.id, body);
    } else {
      await createPeriod(schoolId, scheduleTypeId, body);
    }
  }

  for (const original of originalPeriods) {
    if (!keepIds.has(original.id) && !deletedIds.has(original.id)) {
      await deletePeriod(schoolId, scheduleTypeId, original.id);
    }
  }
}

// Detects a schoolPeriodId reassignment among KEPT period rows within a single
// ScheduleType that reconcilePeriods can't apply in place — rows surviving this
// save (id present in both the original and desired lists) where one row is
// reassigned to a schoolPeriodId that a DIFFERENT kept row still holds at save
// start. Unlike the dropped-row case reconcilePeriods already clears
// just-in-time, no kept row here is being deleted to free its slot:
// reconcilePeriods updates rows in place one REST call at a time, and the
// Period_scheduleTypeId_schoolPeriodId_key unique index allows only one live
// Period per schoolPeriodId, so an in-place update into a slot another kept row
// still occupies collides with it (409).
//
// This generalizes the original pairwise-swap check to ANY cycle or chain: a
// simple A<->B swap (2-cycle), a 3-or-more-cycle (A->B->C->A), and a chain
// (A->B, B->C) all share the same defining property — a kept row moves into a
// schoolPeriodId that, at save start, belongs to another kept row. A final-state
// duplicate (two kept rows ending on the same schoolPeriodId) is already
// rejected earlier by handleSave's per-day duplicate check, so what reaches here
// is a genuine permutation whose only obstacle is a transient in-place collision.
// reconcilePeriods applies these in-place updates one REST call at a time in a
// FIXED desiredPeriods order, so whether a collision is real depends on that
// order: a true cycle (every node's target held by another live node) collides on
// whichever row is processed first regardless of ordering and can never be
// avoided, but a chain (e.g. A->B, B->C) has a valid tail-first ordering — its
// occupant row can vacate the target slot before the mover reaches it. We
// therefore replay reconcilePeriods' exact in-order pass and block only a move
// whose target is STILL occupied when its turn comes; an occupant-before-mover
// chain reconciles in place and is intentionally allowed through. The 2-cycle
// case is preserved exactly as a subset.
//
// The same in-order replay also catches a genuinely NEW row (id == null, no
// original slot) whose createPeriod would 409: reconcilePeriods issues that
// create against the target schoolPeriodId, and if a not-yet-moved kept row
// still holds that slot when the create's turn comes, the unique index rejects
// it. So the pass walks the FULL desiredPeriods list (kept rows AND new rows)
// in order rather than the kept-only subset. A new row vacates nothing; it just
// claims its target if free, or collides if a kept row still occupies it.
//
// Returns the conflict (or null if there's none) so the caller can reject the
// save up front, before any REST call is issued:
//   - kind 'kept': a kept row's in-place move — `a` is the moving row's
//     original slot and `b` the still-occupied slot it's moving into; the
//     caller can name both involved periods (the "swapping periods" message).
//   - kind 'new': a new row's create — `b` is the still-occupied target slot,
//     whose current occupant is the kept period on schoolPeriodId `b`, so
//     naming `b` names both the target and the period blocking it.
export type KeptPeriodSwapConflict =
  | { kind: 'kept'; a: number; b: number }
  | { kind: 'new'; b: number };

// Exported solely for direct unit testing of the in-order replay logic — the
// ordering-based new-row collision (a NEW create ordered before a kept row that
// still holds its target slot) is a genuine reconcilePeriods-order gap the
// detector must handle, but today's UI (which only ever appends new rows, after
// every kept row) can't itself produce that ordering, so it can't be exercised
// end-to-end through the rendered modal the way the kept-vs-kept cases are.
export function detectKeptPeriodSwap(
  originalPeriods: PeriodResponse[],
  desiredPeriods: { id: number | null; schoolPeriodId: number | null }[]
): KeptPeriodSwapConflict | null {
  const originalSchoolPeriodByRowId = new Map(
    originalPeriods.map((p) => [p.id, p.schoolPeriodId])
  );
  // A row is KEPT if it survives this save with a known original slot; NEW if
  // it has no id at all. (An id present but absent from the originals map is
  // neither — left untouched, as before.)
  const isKept = (p: {
    id: number | null;
    schoolPeriodId: number | null;
  }): p is { id: number; schoolPeriodId: number | null } =>
    p.id != null && originalSchoolPeriodByRowId.has(p.id);
  // Replay reconcilePeriods' in-order pass over the full desired list. It is
  // built from the same variantData.periods order reconcilePeriods iterates,
  // so this mirrors exactly which in-place updates/creates it applies and in
  // what order. `occupied` holds the schoolPeriodId each KEPT row still
  // occupies; it starts at every kept row's original slot. Dropped rows' slots
  // are intentionally absent — reconcilePeriods clears each just-in-time before
  // the update/create that reuses it, so they never block a claim here.
  const occupied = new Set(
    desiredPeriods
      .filter(isKept)
      .map((p) => originalSchoolPeriodByRowId.get(p.id))
      .filter((id): id is number => id != null)
  );
  for (const row of desiredPeriods) {
    const target = row.schoolPeriodId;
    if (target == null) continue;
    if (isKept(row)) {
      const ownOriginal = originalSchoolPeriodByRowId.get(row.id);
      if (ownOriginal == null) continue;
      if (target === ownOriginal) continue; // not moving
      if (occupied.has(target)) {
        // Target slot is still held by an as-yet-unprocessed kept row when
        // reconcile reaches this row — the 409 it can't avoid. A true cycle
        // always trips here on its first processed row regardless of order; a
        // chain trips only when its occupant is ordered after this mover.
        return { kind: 'kept', a: ownOriginal, b: target };
      }
      // No collision: this row vacates its old slot and claims the target, so
      // a later row may now move into the vacated slot conflict-free — exactly
      // what reconcilePeriods achieves by updating in place, in order.
      occupied.delete(ownOriginal);
      occupied.add(target);
    } else if (row.id == null) {
      // NEW row: createPeriod into `target`. If a kept row still holds that
      // slot when reconcile reaches this create, the unique index rejects it
      // (409). A new row vacates nothing — it only claims its target.
      if (occupied.has(target)) {
        return { kind: 'new', b: target };
      }
      occupied.add(target);
    }
  }
  return null;
}

// Lazily creates a real SchoolPeriod row for any pending (synthetic,
// negative-id) period still present in `periods` at save time -- see
// resolveDefaultPeriods above. Only called from saveAsNew, i.e. only when
// the admin actually saves, so merely opening then cancelling the modal
// never persists a canonical period the admin didn't ask for.
async function resolvePendingSchoolPeriods(
  schoolId: number | string,
  schoolPeriods: EditableSchoolPeriod[],
  periods: ValidatedPeriod[]
): Promise<ValidatedPeriod[]> {
  const schoolPeriodById = new Map(schoolPeriods.map((sp) => [sp.id, sp]));
  const realSchoolPeriods = schoolPeriods.filter((sp) => sp.id >= 0);
  const resolved: ValidatedPeriod[] = [];

  for (const p of periods) {
    if (p.schoolPeriodId == null || p.schoolPeriodId >= 0) {
      resolved.push(p);
      continue;
    }
    const pending = schoolPeriodById.get(p.schoolPeriodId);
    if (!pending) continue;
    const displayOrder = nextDisplayOrder(realSchoolPeriods);
    const created = await createSchoolPeriod(schoolId, {
      name: pending.name,
      displayOrder,
    });
    realSchoolPeriods.push(created);
    resolved.push({ ...p, schoolPeriodId: created.id });
  }

  return resolved;
}

// Maps this modal's in-memory period shape to a createPeriod/replace-variant
// request body, numbering `order` by array position.
function toPeriodCreateBodies(periods: ValidatedPeriod[]): CreatePeriodBody[] {
  return periods.map((p, i) => ({
    schoolPeriodId: p.schoolPeriodId,
    startTime: p.start,
    endTime: p.end,
    order: i + 1,
  }));
}

// Splits a periods array into A-side and B-side buckets by abDay tag -- a
// period tagged 'Both' belongs to both buckets. Shared by
// createFreshScheduleTypes and saveEdit's shape-conversion branch so the
// 'X || Both' filter logic exists in exactly one place.
function bucketPeriodsByAbDay(periods: ValidatedPeriod[]): {
  aPeriods: ValidatedPeriod[];
  bPeriods: ValidatedPeriod[];
} {
  return {
    aPeriods: periods.filter((p) => p.abDay === 'A' || p.abDay === 'Both'),
    bPeriods: periods.filter((p) => p.abDay === 'B' || p.abDay === 'Both'),
  };
}

// Creates brand-new ScheduleType(s) and their Period rows for a list of
// (already schoolPeriodId-resolved) periods — used by saveAsNew ('create'/
// 'duplicate', which always starts from a blank slate). Not atomic — a
// failure partway through (e.g. the 'B' ScheduleType's create, or any
// createPeriod call) leaves whichever ScheduleType(s) already succeeded in
// place; acceptable here since there's no old resource to interleave with.
async function createFreshScheduleTypes(
  schoolId: number | string,
  name: string,
  periods: ValidatedPeriod[]
): Promise<void> {
  const isSplit = isSplitShape(periods);

  if (isSplit) {
    const aType = await createScheduleType(schoolId, { name, abDay: 'A' });
    const bType = await createScheduleType(schoolId, { name, abDay: 'B' });

    const { aPeriods: aBucket, bPeriods: bBucket } = bucketPeriodsByAbDay(periods);
    const aPeriods = toPeriodCreateBodies(aBucket);
    const bPeriods = toPeriodCreateBodies(bBucket);

    for (const body of aPeriods) {
      await createPeriod(schoolId, aType.id, body);
    }

    for (const body of bPeriods) {
      await createPeriod(schoolId, bType.id, body);
    }
    return;
  }

  const scheduleType = await createScheduleType(schoolId, { name });

  for (const body of toPeriodCreateBodies(periods)) {
    await createPeriod(schoolId, scheduleType.id, body);
  }
}

const ScheduleVariantModal = ({
  isOpen,
  onClose,
  onVariantSaved,
  schoolConfig,
  schoolId,
  mode = 'create',
  existingVariant = null,
}: ScheduleVariantModalProps): React.ReactElement | null => {
  const [loading, setLoading] = useState(false);
  const [loadingPeriods, setLoadingPeriods] = useState(false);
  const [variantData, setVariantData] = useState<{
    variantName: string;
    periods: EditablePeriod[];
  }>({
    variantName: '',
    periods: [],
  });

  // Raw Period rows as loaded from the server, kept around so save-time
  // reconciliation can diff against them by id. Empty for 'create'.
  const [originalPeriods, setOriginalPeriods] = useState<{
    single: PeriodResponse[];
    aDay: PeriodResponse[];
    bDay: PeriodResponse[];
  }>({
    single: [],
    aDay: [],
    bDay: [],
  });

  // The school's canonical period registry (SchoolPeriod rows) — fetched
  // whenever the modal opens so ScheduleForm's period selects have options,
  // and grown in place as the admin creates new canonical periods inline
  // (see handleCreateSchoolPeriod) or as 'create' mode's defaults get
  // resolved (see resolveDefaultPeriods).
  const [schoolPeriods, setSchoolPeriods] = useState<EditableSchoolPeriod[]>([]);

  // Lets the admin flip a loaded variant's single-vs-split shape mid-edit
  // (see handleToggleShape). null means "trust the stored shape" (the
  // Boolean(...) check below); once set, it wins over the stored shape so
  // ScheduleForm re-renders with the layout the admin is converting to.
  const [shapeOverride, setShapeOverride] = useState<'split' | 'single' | null>(
    null
  );

  // Guards the 'create'-mode default-period seeding so it runs at most once
  // per modal session — set once defaults are actually generated (either by
  // the load effect below when schoolConfig is ready at open, or by the
  // deferred seed effect once schoolConfig arrives). Prevents re-seeding
  // over an admin's edits (e.g. after they delete a default period) if the
  // schoolConfig prop's identity later changes. Reset when the modal opens.
  const defaultsSeededRef = useRef(false);

  // For edit/duplicate, render the same single-vs-split layout the source
  // variant is actually stored as, rather than trusting the school's
  // current schoolType (which may have changed since this variant was
  // created). For create, there's no stored shape yet, so schoolConfig's
  // schoolType is what decides.
  const effectiveSchoolConfig = useMemo<SchoolConfig | null | undefined>(() => {
    if (!schoolConfig || mode === 'create' || !existingVariant) {
      return schoolConfig;
    }
    const isSplit = shapeOverride
      ? shapeOverride === 'split'
      : isSplitVariant(existingVariant);
    return { ...schoolConfig, schoolType: isSplit ? 'Block' : 'Regular' };
  }, [schoolConfig, mode, existingVariant, shapeOverride]);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    setLoadingPeriods(true);
    setShapeOverride(null);
    defaultsSeededRef.current = false;

    (async () => {
      try {
        if (!schoolId) return;
        const schoolPeriodsRes = await listSchoolPeriods(schoolId);
        if (cancelled) return;
        const currentSchoolPeriods = schoolPeriodsRes.data;
        setSchoolPeriods(currentSchoolPeriods);

        if (mode === 'create') {
          setOriginalPeriods({ single: [], aDay: [], bDay: [] });
          const { resolved, schoolPeriods: withPending } = resolveDefaultPeriods(
            currentSchoolPeriods,
            generateDefaultPeriods(schoolConfig)
          );
          setSchoolPeriods(withPending);
          setVariantData({ variantName: '', periods: resolved });
          // Only mark seeding done if schoolConfig was actually available
          // (non-empty defaults). If it was still null, leave the flag off so
          // the deferred effect below seeds once it arrives.
          if (resolved.length > 0) {
            defaultsSeededRef.current = true;
          }
          return;
        }

        if (!existingVariant) return;

        const { single, aDay, bDay } = existingVariant.scheduleTypeIds || {};

        const [singleRes, aRes, bRes] = await Promise.all([
          single
            ? listPeriods(schoolId, single)
            : Promise.resolve<PeriodListResponse>({ data: [] }),
          aDay
            ? listPeriods(schoolId, aDay)
            : Promise.resolve<PeriodListResponse>({ data: [] }),
          bDay
            ? listPeriods(schoolId, bDay)
            : Promise.resolve<PeriodListResponse>({ data: [] }),
        ]);

        if (cancelled) return;

        const singlePeriods = sortByOrder(singleRes.data);
        const aPeriods = sortByOrder(aRes.data);
        const bPeriods = sortByOrder(bRes.data);

        setOriginalPeriods({
          single: singlePeriods,
          aDay: aPeriods,
          bDay: bPeriods,
        });

        let periods: EditablePeriod[];
        if (single) {
          periods = singlePeriods.map((p) => ({
            schoolPeriodId: p.schoolPeriodId,
            start: p.startTime,
            end: p.endTime,
            abDay: null,
            singleId: p.id,
          }));
        } else {
          periods = pairAAndBPeriods(aPeriods, bPeriods);
        }

        if (mode === 'duplicate') {
          // Duplicating creates brand-new ScheduleType(s)/Period rows, so
          // drop the source ids entirely.
          periods = periods.map(({ singleId, aId, bId, ...rest }) => rest);
        }

        setVariantData({
          variantName: existingVariant.variantName || '',
          periods,
        });
      } catch (error) {
        logError(error, 'loadScheduleVariantPeriods');
        showError(
          getContextualError(
            error,
            undefined,
            'Failed to load schedule type details. Please try again.'
          )
        );
      } finally {
        if (!cancelled) setLoadingPeriods(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, existingVariant, schoolId]);

  // Deferred default-period seeding for 'create' mode. The load effect above
  // deliberately excludes schoolConfig from its deps (re-running it would
  // refetch and reset the whole form), so if the modal opened before
  // schoolConfig was available, generateDefaultPeriods returned [] and the
  // form would otherwise be stranded permanently empty. Once schoolConfig
  // arrives, seed the defaults exactly once — never over an admin's in-progress
  // edits: it fires only while the create form is still empty and the one-shot
  // ref hasn't been tripped, and preserves any name already typed.
  useEffect(() => {
    if (!isOpen || mode !== 'create') return;
    if (loadingPeriods || defaultsSeededRef.current || !schoolConfig) return;
    if (variantData.periods.length > 0) return;

    const defaults = generateDefaultPeriods(schoolConfig);
    if (defaults.length === 0) return;

    const { resolved, schoolPeriods: withPending } = resolveDefaultPeriods(
      schoolPeriods,
      defaults
    );
    defaultsSeededRef.current = true;
    setSchoolPeriods(withPending);
    setVariantData((prev) => ({ ...prev, periods: resolved }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, mode, schoolConfig, loadingPeriods]);

  // Passed to ScheduleForm for its "+ New period..." select option — creates
  // a canonical SchoolPeriod inline and grows the local list so it's
  // immediately selectable without a refetch.
  const handleCreateSchoolPeriod = async (
    name: string
  ): Promise<SchoolPeriodResponse | null> => {
    if (!schoolId) return null;
    try {
      const displayOrder = nextDisplayOrder(schoolPeriods);
      const created = await createSchoolPeriod(schoolId, { name, displayOrder });
      setSchoolPeriods((prev) => [...prev, created]);
      return created;
    } catch (error) {
      logError(error, 'createSchoolPeriodInline');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to create period. Please try again.'
        )
      );
      return null;
    }
  };

  // Creates brand-new ScheduleType(s) (and their Period rows) for 'create'
  // and 'duplicate' modes — both start from a blank slate server-side.
  const saveAsNew = async (
    name: string,
    periods: ValidatedPeriod[]
  ): Promise<void> => {
    if (!schoolId) return;
    const resolvedPeriods = await resolvePendingSchoolPeriods(
      schoolId,
      schoolPeriods,
      periods
    );
    await createFreshScheduleTypes(schoolId, name, resolvedPeriods);
  };

  // Updates the existing ScheduleType(s) in place and diffs their Period
  // rows against what was loaded.
  const saveEdit = async (
    name: string,
    periods: ValidatedPeriod[]
  ): Promise<void> => {
    if (!existingVariant) return;
    if (!schoolId) return;
    const { single, aDay, bDay } = existingVariant.scheduleTypeIds || {};

    // The admin used the "Convert to Split/Single Schedule" toggle
    // (handleToggleShape) to change the variant's shape mid-edit — the
    // stored ScheduleType(s) no longer match what's being saved, so
    // reconciling Periods against them in place doesn't make sense. Instead,
    // atomically replace the old ScheduleType(s) with a freshly-shaped set
    // via PUT .../schedule-types/replace-variant (a single server-side
    // transaction), rather than the old create-then-delete sequence of
    // independent REST calls. A failed call surfaces through handleSave's
    // existing try/catch -> showError; clicking Save again is simply another
    // identical, safe call, since the transaction guarantees the variant is
    // never left partially converted.
    const wasSplit = isSplitVariant(existingVariant);
    const isSplitNow = isSplitShape(periods);

    if (wasSplit !== isSplitNow) {
      const resolvedPeriods = await resolvePendingSchoolPeriods(
        schoolId,
        schoolPeriods,
        periods
      );
      const isSplit = isSplitShape(resolvedPeriods);
      const { aPeriods: aBucket, bPeriods: bBucket } =
        bucketPeriodsByAbDay(resolvedPeriods);

      await replaceScheduleVariant(schoolId, {
        name,
        newScheduleTypes: isSplit
          ? [
              { abDay: 'A', periods: toPeriodCreateBodies(aBucket) },
              { abDay: 'B', periods: toPeriodCreateBodies(bBucket) },
            ]
          : [{ abDay: null, periods: toPeriodCreateBodies(resolvedPeriods) }],
        oldScheduleTypeIds: scheduleTypeIdsOf({ single, aDay, bDay }),
      });
      return;
    }

    if (single) {
      await updateScheduleType(schoolId, single, { name });

      const desired: DesiredPeriod[] = periods.map((p) => ({
        id: p.singleId ?? null,
        schoolPeriodId: p.schoolPeriodId,
        start: p.start,
        end: p.end,
      }));

      await reconcilePeriods(schoolId, single, originalPeriods.single, desired);
      return;
    }

    if (aDay) {
      await updateScheduleType(schoolId, aDay, { name });
    }
    if (bDay) {
      await updateScheduleType(schoolId, bDay, { name });
    }

    const desiredA: DesiredPeriod[] = periods
      .filter((p) => p.abDay === 'A' || p.abDay === 'Both')
      .map((p) => ({
        id: p.aId ?? null,
        schoolPeriodId: p.schoolPeriodId,
        start: p.start,
        end: p.end,
      }));

    const desiredB: DesiredPeriod[] = periods
      .filter((p) => p.abDay === 'B' || p.abDay === 'Both')
      .map((p) => ({
        id: p.bId ?? null,
        schoolPeriodId: p.schoolPeriodId,
        start: p.start,
        end: p.end,
      }));

    if (aDay) {
      await reconcilePeriods(schoolId, aDay, originalPeriods.aDay, desiredA);
    }
    if (bDay) {
      await reconcilePeriods(schoolId, bDay, originalPeriods.bDay, desiredB);
    }
  };

  const handleSave = async (): Promise<void> => {
    const trimmedName = variantData.variantName.trim();
    if (!trimmedName) {
      showError('Schedule type name is required');
      return;
    }

    if (variantData.periods.length === 0) {
      showError('At least one period is required');
      return;
    }

    if (variantData.periods.some((p) => p.schoolPeriodId == null)) {
      showError('Every period needs a period selected');
      return;
    }

    const isSplit = isSplitShape(variantData.periods);
    const buckets: EditablePeriod[][] = isSplit
      ? [
          variantData.periods.filter((p) => p.abDay === 'A' || p.abDay === 'Both'),
          variantData.periods.filter((p) => p.abDay === 'B' || p.abDay === 'Both'),
        ]
      : [variantData.periods];

    const periodLabel = (p: EditablePeriod): string =>
      schoolPeriods.find((sp) => sp.id === p.schoolPeriodId)?.name ?? 'That period';

    for (const bucket of buckets) {
      const seen = new Set<number | null>();
      for (const p of bucket) {
        if (seen.has(p.schoolPeriodId)) {
          const dupName = periodLabel(p);
          showError(
            `"${dupName}" is selected more than once for the same day. Choose a different period for one of them.`
          );
          return;
        }
        seen.add(p.schoolPeriodId);
      }
    }

    for (const p of variantData.periods) {
      if (p.start && p.end && timeToMinutes(p.start) >= timeToMinutes(p.end)) {
        showError(`"${periodLabel(p)}" start time must be before its end time`);
        return;
      }
    }

    const overlapErrors = findTimeOverlaps(
      variantData.periods.map((p) => ({
        start: p.start,
        end: p.end,
        abDay: p.abDay,
        label: periodLabel(p),
      })),
      isSplit
    );
    if (overlapErrors.length > 0) {
      showError(overlapErrors[0]);
      return;
    }

    // Kept-rows schoolPeriodId reassignment: surviving periods whose reassigned
    // schoolPeriodIds cycle/chain among each other (a 2-cycle swap, a 3+-cycle,
    // or a chain) can't be reconciled in place (see detectKeptPeriodSwap) and
    // would 409 partway through the save. Only the in-place reconcile path
    // is affected — saveAsNew ('create'/'duplicate') writes fresh rows, and a
    // shape conversion goes through the transactional replace-variant endpoint
    // — so this is checked only for an in-place edit of an unchanged shape.
    if (mode === 'edit' && isSplitVariant(existingVariant) === isSplit) {
      const rowsFor = (
        side: 'A' | 'B' | null
      ): { id: number | null; schoolPeriodId: number | null }[] =>
        isSplit
          ? variantData.periods
              .filter((p) => p.abDay === side || p.abDay === 'Both')
              .map((p) => ({
                id: (side === 'A' ? p.aId : p.bId) ?? null,
                schoolPeriodId: p.schoolPeriodId,
              }))
          : variantData.periods.map((p) => ({
              id: p.singleId ?? null,
              schoolPeriodId: p.schoolPeriodId,
            }));

      const swapChecks = isSplit
        ? [
            { original: originalPeriods.aDay || [], desired: rowsFor('A') },
            { original: originalPeriods.bDay || [], desired: rowsFor('B') },
          ]
        : [{ original: originalPeriods.single || [], desired: rowsFor(null) }];

      for (const { original, desired } of swapChecks) {
        const conflict = detectKeptPeriodSwap(original, desired);
        if (conflict) {
          const nameFor = (schoolPeriodId: number): string =>
            schoolPeriods.find((sp) => sp.id === schoolPeriodId)?.name ??
            'a period';
          showError(
            conflict.kind === 'kept'
              ? `"${nameFor(conflict.a)}" and "${nameFor(conflict.b)}" are swapping periods in a single save, which can't be applied at once. Save one of those changes first, then make the other.`
              : `A new period can't be added to "${nameFor(conflict.b)}" while another period is still assigned to it in this save. Save that change first, then add the new period.`
          );
          return;
        }
      }
    }

    setLoading(true);
    try {
      const periods = variantData.periods.filter(
        (p): p is ValidatedPeriod => p.schoolPeriodId != null
      );
      if (mode === 'edit') {
        await saveEdit(trimmedName, periods);
      } else {
        await saveAsNew(trimmedName, periods);
      }

      showSuccess(
        mode === 'edit'
          ? 'Schedule type updated'
          : 'Schedule type created'
      );
      onVariantSaved();
      onClose();
    } catch (error) {
      logError(error, 'saveScheduleVariant');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to save schedule type. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!existingVariant || mode === 'create') return;
    if (!schoolId) return;

    if (
      !window.confirm(
        `Are you sure you want to delete the schedule type "${existingVariant.variantName}"? This cannot be undone.`
      )
    ) {
      return;
    }

    setLoading(true);
    try {
      const { single, aDay, bDay } = existingVariant.scheduleTypeIds || {};
      const idsToDelete = scheduleTypeIdsOf({ single, aDay, bDay });

      for (const id of idsToDelete) {
        await deleteScheduleType(schoolId, id);
      }

      showSuccess('Schedule type deleted');
      onVariantSaved();
      onClose();
    } catch (error) {
      logError(error, 'deleteScheduleVariant');
      showError(
        getContextualError(
          error,
          undefined,
          'Failed to delete schedule type. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Lets an admin editing a loaded variant flip its single-vs-split shape.
  // effectiveSchoolConfig picks up shapeOverride and re-renders ScheduleForm
  // with the other layout, which is otherwise unreachable mid-edit since
  // effectiveSchoolConfig would just keep re-deriving the stored shape.
  const handleToggleShape = (): void => {
    const currentlySplit = isSplitShape(variantData.periods);
    const targetSplit = !currentlySplit;

    if (currentlySplit) {
      // Split -> single. Always ask before dropping B-only content --
      // unconditionally, not just on some paths: collapseToSingle always
      // filters out abDay === 'B' periods, whether this toggle is a
      // first-time conversion or a revert to a variant that was
      // originally stored as single, and whether or not anything else
      // was edited. (The pre-unification code only asked on the 'genuine
      // convert' path, not the 'revert to originally-single' path --
      // meaning a B-only period added after an earlier split toggle used
      // to get silently dropped with zero warning on a revert. This
      // closes that gap too.)
      const hasBOnlyPeriods = variantData.periods.some((p) => p.abDay === 'B');
      if (
        hasBOnlyPeriods &&
        !window.confirm(
          'Converting to a single schedule will discard every B-day-only period. This cannot be undone. Continue?'
        )
      ) {
        return;
      }
      setVariantData((prev) => ({
        ...prev,
        periods: collapseToSingle(prev.periods),
      }));
    } else {
      // Single -> split. expandToSplit recovers each period's real
      // original A/B/Both tag (and aId/bId) directly from the fields it
      // already carries, regardless of whether this period or any other
      // one was edited -- so there's nothing left to compare or branch on.
      setVariantData((prev) => ({
        ...prev,
        periods: expandToSplit(prev.periods),
      }));
    }
    setShapeOverride(targetSplit ? 'split' : 'single');
  };

  if (!isOpen) return null;

  const getModalTitle = (): string => {
    switch (mode) {
      case 'create':
        return 'Create Schedule Type';
      case 'edit':
        return 'Edit Schedule Type';
      case 'duplicate':
        return 'Duplicate Schedule Type';
      default:
        return 'Schedule Type';
    }
  };

  return (
    <Modal
      className={styles['schedule-variant-modal']}
      onClose={loading ? undefined : onClose}
    >
      <ModalHeader title={getModalTitle()} onClose={onClose} disabled={loading} />

      <ModalBody>
        <div className={styles['variant-name-section']}>
          <label>
            Schedule Type Name:
            <input
              type="text"
              value={variantData.variantName}
              onChange={(e) =>
                setVariantData((prev) => ({
                  ...prev,
                  variantName: e.target.value,
                }))
              }
              placeholder="e.g., Testing Schedule, Half Day, etc."
              className={styles['variant-name-input']}
              disabled={loading}
            />
          </label>
          {mode === 'edit' && !loadingPeriods && (
            <button
              type="button"
              onClick={handleToggleShape}
              className={styles['btn-secondary']}
              disabled={loading}
            >
              {isSplitShape(variantData.periods)
                ? 'Convert to Single Schedule'
                : 'Convert to Split (A/B) Schedule'}
            </button>
          )}
        </div>

        {loadingPeriods ? (
          <div className="loading">Loading schedule details...</div>
        ) : (
          <ScheduleForm
            periods={variantData.periods}
            onPeriodsChange={(periods) =>
              setVariantData((prev) => ({ ...prev, periods }))
            }
            schoolConfig={effectiveSchoolConfig}
            schoolPeriods={schoolPeriods}
            onCreateSchoolPeriod={handleCreateSchoolPeriod}
            disabled={loading}
          />
        )}
      </ModalBody>

      <ModalFooter
        left={
          mode !== 'create' && existingVariant && (
            <button
              onClick={handleDelete}
              className={styles['btn-danger']}
              disabled={loading}
            >
              {loading ? 'Deleting...' : 'Delete Schedule Type'}
            </button>
          )
        }
      >
        <button
          onClick={onClose}
          className={styles['btn-secondary']}
          disabled={loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          className={styles['btn-primary']}
          disabled={
            loading || loadingPeriods || !variantData.variantName.trim()
          }
        >
          {loading
            ? 'Saving...'
            : mode === 'create'
              ? 'Create Schedule Type'
              : 'Save Changes'}
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default ScheduleVariantModal;
